/**
 * 팝오버 배타성 + 공용 바깥클릭/Esc 처리.
 *
 * 문제(사용자 신고): "알림 눌렀을 때 나오는 popover 가 다른 메뉴가 뜨면 안 닫힌다".
 * 각 모듈이 자기 문서 리스너로 바깥 클릭을 처리했는데, 트리거들이 stopPropagation()
 * 을 부르는 탓에(벨·사용자 메뉴) 다른 메뉴를 열어도 이미 열린 팝오버는 그대로 남았다.
 *
 * 여기서는 (1) 새 팝오버를 열 때 이전 것을 반드시 닫고, (2) 바깥 클릭과 Esc 를 문서
 * 캡처 단계의 리스너 한 벌로만 처리한다 — 캡처는 대상 요소보다 먼저 도니 어떤
 * stopPropagation 도 막지 못한다.
 *
 * 사용법:
 *   openExclusive(id, closeFn, { roots })  열면서 다른 팝오버를 닫는다.
 *     roots: Element | 선택자 | 그 배열 | 함수(() => Element[]) — 이 안의 클릭은 "바깥"이 아니다.
 *     manual: true 면 배타성만 쓰고 바깥클릭·Esc 는 모듈이 스스로 처리한다 (하위 메뉴가
 *             있는 컨텍스트 메뉴처럼 판정이 복잡한 경우).
 *   closedExclusive(id)  모듈이 스스로 닫았을 때 등록만 지운다 (closeFn 은 다시 부르지 않는다).
 *   closePopovers()      전부 닫는다.
 */
const open = new Map(); // id → { close, roots, manual }
let wired = false;

function elementsOf(entry) {
    const raw = typeof entry.roots === 'function' ? entry.roots() : entry.roots;
    if (!raw) return [];
    return (Array.isArray(raw) ? raw : [raw])
        .map((el) => (typeof el === 'string' ? document.querySelector(el) : el))
        .filter((el) => el && el.contains);
}

function closeEntry(id) {
    const entry = open.get(id);
    if (!entry) return;
    open.delete(id); // 먼저 지운다 — closeFn 이 closedExclusive 를 다시 불러도 재귀하지 않게
    try { entry.close(); } catch (err) { /* 닫기 실패가 다른 팝오버를 막지 않게 */ }
}

function wire() {
    if (wired) return;
    wired = true;
    document.addEventListener('click', (e) => {
        Array.from(open.entries()).forEach(([id, entry]) => {
            if (entry.manual) return;
            if (elementsOf(entry).some((el) => el.contains(e.target))) return;
            closeEntry(id);
        });
    }, true);
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        Array.from(open.entries()).forEach(([id, entry]) => { if (!entry.manual) closeEntry(id); });
    }, true);
}

export function openExclusive(id, closeFn, opts) {
    const options = opts || {};
    wire();
    Array.from(open.keys()).forEach((other) => { if (other !== id) closeEntry(other); });
    open.set(id, { close: closeFn, roots: options.roots, manual: !!options.manual });
}

export function closedExclusive(id) {
    open.delete(id);
}

export function closePopovers(except) {
    Array.from(open.keys()).forEach((id) => { if (id !== except) closeEntry(id); });
}

export function isPopoverOpen(id) {
    return open.has(id);
}

// 열린 팝오버가 하나라도 있는가 — 비배타 바텀 시트(mobile-shell.js)가 Esc 를 양보할 때 쓴다.
export function anyPopoverOpen() {
    return open.size > 0;
}
